import React, { useState } from 'react';
import axios from 'axios';

/**
 * Retrieve-and-test control for a Secrets Manager backed credential.
 *
 * The endpoint answers with an ordered chain of stages rather than one
 * pass/fail, because "the test failed" is not actionable on its own: a bad
 * secret name, the wrong region, a missing instance profile, an IAM gap, a KMS
 * gap and a mistyped key name all look identical from outside. Each stage that
 * fails carries what was expected, what was found instead, and the fix — and
 * those are shown prominently, not folded away.
 *
 * Props
 *   endpoint  POST URL that runs the chain
 *   payload   () => object of unsaved form values, so the user can test what is
 *             on screen rather than only what has been saved
 *   disabled  blocks the button when required fields are still empty
 *   label     button text
 */

function StatusDot({ status }) {
  const color = status === 'pass' ? 'var(--success)'
    : status === 'fail' ? 'var(--danger, #d93025)'
      : 'var(--text-muted)';
  const glyph = status === 'pass' ? '✓' : status === 'fail' ? '✕' : '·';
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
        border: `1.5px solid ${color}`, color,
        fontSize: 11, fontWeight: 700, lineHeight: 1,
      }}
    >
      {glyph}
    </span>
  );
}

function Stage({ check }) {
  const failed = check.status === 'fail';
  const muted = check.status === 'skip';
  return (
    <li style={{ display: 'flex', gap: 10, padding: '7px 0', alignItems: 'flex-start' }}>
      <StatusDot status={check.status} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: 13,
          fontWeight: failed ? 600 : 500,
          color: muted ? 'var(--text-muted)' : 'var(--text-primary)',
        }}>
          {check.label}
          {muted && <span style={{ fontWeight: 400 }}> — not reached</span>}
        </div>

        {check.detail && !failed && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, wordBreak: 'break-word' }}>
            {check.detail}
          </div>
        )}

        {failed && (
          <dl style={{
            margin: '6px 0 0', padding: '8px 10px',
            background: 'var(--bg-subtle, rgba(217,48,37,0.06))',
            borderLeft: '3px solid var(--danger, #d93025)',
            borderRadius: 4, fontSize: 12, lineHeight: 1.5,
          }}>
            {[
              ['Expected', check.expected],
              ['Found', check.actual],
              ['Fix', check.fix],
            ].filter(([, v]) => v).map(([term, value]) => (
              <div key={term} style={{ display: 'flex', gap: 8, marginBottom: 2 }}>
                <dt style={{
                  margin: 0, fontWeight: 600, minWidth: 62, flexShrink: 0,
                  color: 'var(--text-secondary)',
                }}>
                  {term}
                </dt>
                <dd style={{ margin: 0, wordBreak: 'break-word' }}>{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </li>
  );
}

export default function CredentialTest({ endpoint, payload, disabled, label = 'Retrieve & test' }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  async function run() {
    setRunning(true);
    setResult(null);
    setError('');
    try {
      const res = await axios.post(endpoint, payload ? payload() : {});
      setResult(res.data);
    } catch (err) {
      // A 4xx from this endpoint still carries a chain when the server produced
      // one; anything else is a transport or auth problem with our own API.
      if (err.response?.data?.checks) setResult(err.response.data);
      else setError(err.response?.data?.error || 'Could not run the test — the application API did not respond.');
    } finally {
      setRunning(false);
    }
  }

  const failed = result?.checks?.find(c => c.status === 'fail');

  return (
    <div>
      <button type="button" className="btn btn-secondary" onClick={run} disabled={running || disabled}>
        {running ? 'Testing…' : label}
      </button>

      {error && (
        <div className="alert alert-error" style={{ marginTop: 12 }}>{error}</div>
      )}

      {result && (
        <div style={{
          marginTop: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius-lg, 8px)',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 14px',
            borderBottom: '1px solid var(--border)',
            background: result.ok
              ? 'var(--success-bg, rgba(26,127,55,0.08))'
              : 'var(--danger-bg, rgba(217,48,37,0.08))',
            fontSize: 13, fontWeight: 600,
            color: result.ok ? 'var(--success)' : 'var(--danger, #d93025)',
          }}>
            {result.ok
              ? 'Credential retrieved and accepted.'
              : `Failed at: ${failed ? failed.label : 'unknown stage'}`}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: '8px 14px 12px' }}>
            {(result.checks || []).map(c => <Stage key={c.step} check={c} />)}
          </ul>
        </div>
      )}
    </div>
  );
}
